import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom/dist";
import Table from "../../../../core/pagination/datatable";
import CommonFooter from "../../../../core/common/footer/commonFooter";
import { ROLL_CALL_API_BASE_URL } from "../RollCallConfig/RollCallConfig";
import { Spin } from "antd";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import ImageWithBasePath from "../../../../core/img/imagewithbasebath";
import { RotateCcw } from "feather-icons-react/build/IconComponents";

const EmployeesByShift = () => {
  const [shiftTypes, setShiftTypes] = useState([]);
  const [selectedShift, setSelectedShift] = useState(null);
  const [data, setData] = useState([]);
  const [shiftsLoading, setShiftsLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [tokenData, setTokenData] = useState(null);
  const [superIdData, setSuperIdData] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [perm, setPerm] = useState({
    Read: false,
    Write: false,
    Delete: false,
    Export: false,
  });

  useEffect(() => {
    const stored = sessionStorage.getItem("rollcall-permissions");
    if (!stored) return;
    const map = JSON.parse(stored);
    const path = location.pathname.slice(1);
    const perms = map[path];
    if (perms) {
      setPerm({
        Read: perms.canRead,
        Write: perms.canWrite,
        Delete: perms.canDelete,
        Export: perms.canExport,
      });
    }
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    const superId = sessionStorage.getItem("superId");
    if (token && superId) {
      setTokenData(token);
      setSuperIdData(superId);
    } else {
      console.warn("User data not found in sessionStorage");
      navigate("/signin");
    }
  }, [navigate]);

  // Fetch shift types for dropdown
  const fetchShiftTypes = async () => {
    if (!tokenData || !superIdData) return;
    setShiftsLoading(true);
    try {
      const response = await fetch(
        `${ROLL_CALL_API_BASE_URL}portal/shifts/getShiftsTypes?superid=${superIdData}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokenData}`,
          },
        },
      );
      const result = await response.json();
      if (result.status) {
        setShiftTypes(result.ResultData);
        // Auto-select first shift
        if (result.ResultData.length > 0) {
          setSelectedShift(result.ResultData[0]);
        }
      } else {
        console.error("Failed to fetch shift types:", result.ErrorMsg);
      }
    } catch (error) {
      console.error("Error fetching shift types:", error.message);
    } finally {
      setShiftsLoading(false);
    }
  };

  // Fetch employees by selected shift
  const fetchEmployeesByShift = async (shiftId) => {
    if (!tokenData || !superIdData || !shiftId) return;
    setDataLoading(true);
    try {
      const response = await fetch(
        `${ROLL_CALL_API_BASE_URL}portal/shifts/getEmpByShift?shiftid=${shiftId}&superid=${superIdData}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokenData}`,
          },
        },
      );
      const result = await response.json();
      if (result.status) {
        setData(result.ResultData);
      } else {
        setData([]);
        console.error("Failed to fetch employees:", result.ErrorMsg);
      }
    } catch (error) {
      console.error("Error fetching employees by shift:", error.message);
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    if (tokenData && superIdData) {
      fetchShiftTypes();
    }
  }, [tokenData, superIdData]);

  useEffect(() => {
    if (selectedShift) {
      fetchEmployeesByShift(selectedShift.shiftid);
    }
  }, [selectedShift]);

  const handleShiftChange = (e) => {
    const shiftId = parseInt(e.target.value);
    const shift = shiftTypes.find((s) => s.shiftid === shiftId);
    setSelectedShift(shift || null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatTime = (timeString) => {
    if (!timeString) return "N/A";
    const [hours, minutes] = timeString.split(":");
    const h = parseInt(hours);
    const ampm = h >= 12 ? "PM" : "AM";
    const displayHour = h % 12 === 0 ? 12 : h % 12;
    return `${String(displayHour).padStart(2, "0")}:${minutes} ${ampm}`;
  };

  const handleDownloadExcel = () => {
    if (!data || data.length === 0) return;
    const wb = XLSX.utils.book_new();
    const wsData = data.map((item, index) => ({
      "S.No": index + 1,
      "Badge ID": item.Badge || "N/A",
      Name: item.UserName || "N/A",
      Designation: item.Designation || "N/A",
      Department: item.Departmentname || "N/A",
      Shift: item.ShiftName || "N/A",
      "Start Date": formatDate(item.MaxStartDate),
      "End Date": formatDate(item.EndDate),
    }));
    const ws = XLSX.utils.json_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "Employees By Shift");
    XLSX.writeFile(wb, `Employees_${selectedShift?.Name || "Shift"}.xlsx`);
  };

  const generatePDF = () => {
    if (!data || data.length === 0) return;
    const doc = new jsPDF();
    const generatedDate = new Date().toLocaleString();
    const tableColumn = [
      "S.No",
      "Badge ID",
      "Name",
      "Designation",
      "Department",
      "Shift",
      "Start Date",
      "End Date",
    ];
    const tableRows = data.map((item, index) => [
      index + 1,
      item.Badge || "N/A",
      item.UserName || "N/A",
      item.Designation || "N/A",
      item.Departmentname || "N/A",
      item.ShiftName || "N/A",
      formatDate(item.MaxStartDate),
      formatDate(item.EndDate),
    ]);
    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 18,
      didDrawPage: function () {
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        doc.setFontSize(12);
        doc.text(`Employees By Shift - ${selectedShift?.Name || ""}`, 14, 10);
        doc.setFontSize(9);
        doc.text(`Generated: ${generatedDate}`, pageWidth - 14, 10, {
          align: "right",
        });
        doc.text(
          "RollCall Attendance System. Powered by Perennial Code IT Consultants",
          pageWidth / 2,
          pageHeight - 8,
          { align: "center" },
        );
      },
    });
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFontSize(9);
      doc.text(`Page ${i} of ${totalPages}`, pageWidth - 14, pageHeight - 8, {
        align: "right",
      });
    }
    doc.save(`Employees_${selectedShift?.Name || "Shift"}.pdf`);
  };

  const handleReload = () => {
    window.location.reload();
  };

  const columns = [
    {
      title: "Badge",
      dataIndex: "Badge",
      sorter: (a, b) => (a.Badge || "").localeCompare(b.Badge || ""),
      render: (text) => text || "N/A",
    },
    {
      title: "Name",
      dataIndex: "UserName",
      sorter: (a, b) => (a.UserName || "").localeCompare(b.UserName || ""),
      render: (text) => (
        <span>{text?.length > 20 ? `${text.substring(0, 20)}...` : text}</span>
      ),
    },
    {
      title: "Designation",
      dataIndex: "Designation",
      sorter: (a, b) =>
        (a.Designation || "").localeCompare(b.Designation || ""),
      render: (text) => (
        <span>
          {text?.length > 18 ? `${text.substring(0, 18)}...` : text || "N/A"}
        </span>
      ),
    },
    {
      title: "Department",
      dataIndex: "Departmentname",
      sorter: (a, b) =>
        (a.Departmentname || "").localeCompare(b.Departmentname || ""),
      render: (text) => (
        <span>
          {text?.length > 18 ? `${text.substring(0, 18)}...` : text || "N/A"}
        </span>
      ),
    },
    // {
    //   title: "Shift",
    //   dataIndex: "ShiftName",
    //   sorter: (a, b) => (a.ShiftName || "").localeCompare(b.ShiftName || ""),
    //   render: (text) => text || "N/A",
    // },
    {
      title: "Start Date",
      dataIndex: "MaxStartDate",
      sorter: (a, b) => new Date(a.MaxStartDate) - new Date(b.MaxStartDate),
      render: (text) => formatDate(text),
    },
    {
      title: "End Date",
      dataIndex: "EndDate",
      sorter: (a, b) => new Date(a.EndDate) - new Date(b.EndDate),
      render: (text) => formatDate(text),
    },
  ];

  const renderTooltip = (props) => (
    <Tooltip id="pdf-tooltip" {...props}>
      Pdf
    </Tooltip>
  );
  const renderExcelTooltip = (props) => (
    <Tooltip id="excel-tooltip" {...props}>
      Excel
    </Tooltip>
  );
  const renderRefreshTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Refresh
    </Tooltip>
  );

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/rollcall/dashboard">
                        <i className="fas fa-home fa-lg" />
                      </Link>
                    </li>
                    <h4 className="breadcrumb-item" aria-current="page">
                      Employees By Shift
                    </h4>
                  </ol>
                </nav>
                <h6>View employees grouped by their assigned shift</h6>
              </div>
            </div>
            <ul className="table-top-head">
              {perm?.Export && (
                <>
                  <li>
                    <OverlayTrigger placement="top" overlay={renderTooltip}>
                      <Link onClick={generatePDF}>
                        <ImageWithBasePath
                          src="assets/img/icons/pdf.svg"
                          alt="img"
                        />
                      </Link>
                    </OverlayTrigger>
                  </li>
                  <li>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderExcelTooltip}
                    >
                      <Link onClick={handleDownloadExcel}>
                        <ImageWithBasePath
                          src="assets/img/icons/excel.svg"
                          alt="img"
                        />
                      </Link>
                    </OverlayTrigger>
                  </li>
                </>
              )}
              <li onClick={handleReload}>
                <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
                  <Link data-bs-toggle="tooltip" data-bs-placement="top">
                    <RotateCcw />
                  </Link>
                </OverlayTrigger>
              </li>
            </ul>
            {perm?.Write && (
              <div className="page-btn mb-3">
                <Link
                  to="/rollcall/shifts"
                  className="btn btn-success btn-sm"
                  title="Assign Shift"
                >
                  <i className="ti ti-circle-plus fs-4"></i>
                </Link>
              </div>
            )}
          </div>

          {perm.Read ? (
            <div className="card table-list-card no-search">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                <div>
                  <div className="search-set "></div>
                  <div className="d-flex align-items-center flex-wrap row-gap-3">
                    <div className="d-flex align-items-center gap-2">
                      <label className="form-label mb-0 fw-semibold text-nowrap"></label>
                      {shiftsLoading ? (
                        <Spin size="small" />
                      ) : (
                        <select
                          className="form-select form-select-sm"
                          style={{ minWidth: 200 }}
                          value={selectedShift?.shiftid || ""}
                          onChange={handleShiftChange}
                        >
                          {shiftTypes.map((shift) => (
                            <option key={shift.shiftid} value={shift.shiftid}>
                              {shift.Name}
                              {/* ({formatTime(shift.StartTime)} –{" "}
                              {formatTime(shift.EndTime)}) */}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    {selectedShift && (
                      <div className="d-flex align-items-center gap-2 ms-3">
                        <span className="badge bg-primary-subtle text-primary px-3 py-2">
                          <i className="fas fa-clock me-1" />
                          {formatTime(selectedShift.StartTime)} –{" "}
                          {formatTime(selectedShift.EndTime)}
                        </span>
                        <span className="badge bg-success-subtle text-success px-3 d-none py-2">
                          <i className="fas fa-users me-1" />
                          {data.length} Employee{data.length !== 1 ? "s" : ""}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="card-body">
                <div className="table-responsive brand-table">
                  {dataLoading ? (
                    <div className="text-center my-4">
                      <Spin size="large" />
                      <p>Loading data...</p>
                    </div>
                  ) : (
                    <div className="table-responsive brand-table">
                      <Table columns={columns} dataSource={data} pgno={true} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body d-flex justify-content-center">
                <h1>You don&apos;t have access to this page</h1>
              </div>
            </div>
          )}
        </div>
        <CommonFooter />
      </div>
    </div>
  );
};

export default EmployeesByShift;
